/**
 * StandingsRowDetail — what opens under a standings row: the contestant's
 * per-rodeo → per-event ledger, the same shape the Season Roster's contestant
 * detail shows, fetched only when the row is expanded so the board itself
 * never ships breakdowns.
 *
 * Additional winnings are one line at the top, amount only — the delegate's
 * notes never reach this surface (the server's closed schema sees to that).
 * Under an event type filter the server re-totals it to that type's external
 * results and filters the rows.
 *
 * External rows — results a delegate entered from rodeos HighCall did not run
 * — are tinted and badged "External" so they read differently from scored
 * results (see season-breakdown.ts).
 */

import { Fragment } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useSeasonStandingsContestant,
  type SeasonBreakdownRow,
} from '@/api/extensions/useSeasonWinningsOps';
import { formatCurrency, formatDate, getOrdinal } from '@/utils/formatters';
import {
  EXTERNAL_ROW_CLASS,
  breakdownRowKey,
  groupByRodeo,
  scopeLabel,
} from './season-breakdown';

export function StandingsRowDetail({
  orgId,
  year,
  userId,
  eventTypeId,
}: {
  orgId: string;
  year: number;
  userId: string;
  eventTypeId: string | null;
}) {
  const { data, isLoading, error } = useSeasonStandingsContestant(
    orgId,
    year,
    userId,
    eventTypeId,
    true,
  );

  if (isLoading) {
    return (
      <div className="space-y-2 py-1" aria-busy="true">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <p className="py-2 text-sm text-destructive">
        Could not load this contestant's results.
      </p>
    );
  }

  const groups = groupByRodeo(data.breakdown);
  const additional = data.additional_winnings;

  return (
    <div className="space-y-3 py-1" data-testid="standings-row-detail">
      {additional != null && additional > 0 && (
        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="font-medium">Additional winnings</span>
          <span className="tabular-nums font-medium text-green-600">
            {formatCurrency(additional)}
          </span>
        </div>
      )}

      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {eventTypeId ? 'No results in this event.' : 'No results at managed rodeos yet.'}
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Payout</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Placement</TableHead>
                  <TableHead className="text-right">Won</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <Fragment key={group.rodeo_id}>
                    <TableRow className={group.external ? EXTERNAL_ROW_CLASS : 'bg-muted hover:bg-muted'}>
                      <TableCell colSpan={5} className="font-semibold">
                        {group.rodeo_name}
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {formatDate(group.start_date) || '--'}
                        </span>
                        {group.external && <ExternalBadge />}
                      </TableCell>
                    </TableRow>
                    {group.rows.map((row) => (
                      <TableRow
                        key={breakdownRowKey(row)}
                        className={row.external ? EXTERNAL_ROW_CLASS : undefined}
                        data-external={row.external ? 'true' : undefined}
                      >
                        <TableCell className="pl-8 font-medium">
                          {row.event_name}
                          {row.external && <ExternalBadge />}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{scopeLabel(row)}</TableCell>
                        <TableCell>{formatDate(row.start_date) || '--'}</TableCell>
                        <TableCell className="text-right">
                          <PlacementBadge placement={row.placement} />
                        </TableCell>
                        <TableCell className="text-right">
                          <PrizeCell row={row} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-4 md:hidden">
            {groups.map((group) => (
              <div key={group.rodeo_id} className="space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold">
                    {group.rodeo_name}
                    {group.external && <ExternalBadge />}
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(group.start_date) || '--'}
                  </span>
                </div>
                {group.rows.map((row) => (
                  <div
                    key={breakdownRowKey(row)}
                    className={`space-y-2 rounded-lg border p-3 ${row.external ? EXTERNAL_ROW_CLASS : ''}`}
                    data-external={row.external ? 'true' : undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-medium">{row.event_name}</h4>
                      <PlacementBadge placement={row.placement} />
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-muted-foreground">{scopeLabel(row)}</span>
                      <PrizeCell row={row} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Marks a result a delegate entered from a rodeo HighCall did not run. */
function ExternalBadge() {
  return (
    <Badge
      variant="outline"
      className="ml-2 border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 align-middle"
    >
      External
    </Badge>
  );
}

/** "--" means the payout didn't reach them, not that data is missing. */
function PlacementBadge({ placement }: { placement: number | null }) {
  if (!placement) return <span className="text-muted-foreground">--</span>;
  return (
    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
      {getOrdinal(placement)}
    </span>
  );
}

/**
 * The contestant's OWN share, with the undivided team figure alongside when a
 * team roping payout was split — the same convention the dashboard's event
 * history uses, and the reason a season total can't be read off the team
 * amounts.
 */
function PrizeCell({ row }: { row: SeasonBreakdownRow }) {
  if (!row.prize_amount) return <span className="text-muted-foreground">--</span>;
  return (
    <span className="font-medium text-green-600">
      {formatCurrency(row.prize_amount)}
      {row.team_prize ? (
        <span className="ml-1 text-xs font-normal text-muted-foreground">
          ({formatCurrency(row.team_prize)} team)
        </span>
      ) : null}
    </span>
  );
}
